import { getCurrentQuarter } from '../hooks/useBestEarnRatesV2';
import {
  isQuarterlyCategoriesEarnRate,
  isSelectableCategoriesEarnRate,
  isFixedCategoryGroupEarnRate,
  getSelectableCategories,
  isAdvancedCategory,
} from '../../types/utils';

// Utility function to flatten rewards earn rates by handling selectable and quarterly categories
export const flattenRewardsEarnRates = (
  rewardsEarnRates: any[],
  currentSelectedCategories?: string[],
  rewardsType?: string
) => {
  if (!rewardsEarnRates || !Array.isArray(rewardsEarnRates)) {
    return [];
  }

  const flattenedRates: Array<{
    category: string;
    earnRate: any;
    rewardsType?: string;
    isSelectable?: boolean;
    isQuarterly?: boolean;
    isFixedGroup?: boolean;
    description?: string;
    spendCap?: number | null;
    spendCapResetFrequency?: string | null;
  }> = [];

  rewardsEarnRates.forEach((rate) => {
    if (isSelectableCategoriesEarnRate(rate)) {
      // Handle selectable categories
      if (currentSelectedCategories && currentSelectedCategories.length > 0) {
        // Use user's selected categories
        currentSelectedCategories.forEach((category) => {
          flattenedRates.push({
            category,
            earnRate: rate.earnRate,
            rewardsType,
            isSelectable: true,
          });
        });
      } else if (rate.selectableCategories.categories.length > 0) {
        // Use first category if no selection made
        const firstCategory = rate.selectableCategories.categories[0];
        const categoryName =
          typeof firstCategory === 'string'
            ? firstCategory
            : firstCategory.categories?.[0] || 'Unknown';

        flattenedRates.push({
          category: categoryName,
          earnRate: rate.earnRate,
          rewardsType,
          isSelectable: true,
        });
      }
    } else if (isQuarterlyCategoriesEarnRate(rate)) {
      // Handle quarterly categories
      const currentQuarter = getCurrentQuarter();

      rate.selectableCategories.categories
        ?.filter((cat: any) => cat.active_quarter === currentQuarter)
        ?.forEach((cat: any) => {
          const categories = isAdvancedCategory(cat) ? cat.categories : [cat];
          categories.forEach((category: string) => {
            flattenedRates.push({
              category,
              earnRate: rate.earnRate,
              rewardsType,
              isQuarterly: true,
            });
          });
        });
    } else if (isFixedCategoryGroupEarnRate(rate)) {
      // Handle fixed category groups
      rate.selectableCategories.categories.forEach((cat: any) => {
        const categories = isAdvancedCategory(cat) ? cat.categories : [cat];
        categories.forEach((category: string) => {
          flattenedRates.push({
            category,
            earnRate: rate.earnRate,
            rewardsType,
            isFixedGroup: true,
          });
        });
      });
    } else {
      // Regular earn rate - remap travel portal rates to their own category
      flattenedRates.push({
        category: rate.requiresTravelPortal ? 'travel.travelportal' : rate.category,
        earnRate: rate.earnRate,
        rewardsType,
        description: rate.description,
        spendCap: rate.spendCap,
        spendCapResetFrequency: rate.spendCapResetFrequency,
      });
    }
  });

  // Deduplicate by category, keeping the highest earn rate
  const seen = new Map<string, (typeof flattenedRates)[number]>();
  for (const rate of flattenedRates) {
    const existing = seen.get(rate.category);
    if (!existing || rate.earnRate > existing.earnRate) {
      seen.set(rate.category, rate);
    }
  }
  return Array.from(seen.values());
};
